import React, { useState } from 'react';
import { Settings } from 'react-feather';

const checkboxStyle = {
    cursor: 'pointer',
    accentColor: 'var(--button-bg)',
    marginTop: '2px',
    flexShrink: 0,
};

function SectionLabel({ children, first }) {
    return (
        <div style={{
            margin: first ? '0 0 8px' : '12px 0 8px',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'var(--text-secondary)',
        }}>
            {children}
        </div>
    );
}

function CheckRow({ checked, onChange, label, hint, disabled }) {
    return (
        <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            fontSize: '13px',
            cursor: disabled ? 'default' : 'pointer',
            marginBottom: '8px',
            color: 'var(--text-color)',
            opacity: disabled ? 0.75 : 1,
        }}>
            <input
                type="checkbox"
                checked={!!checked}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
                style={checkboxStyle}
            />
            <span>
                {label}
                {hint ? (
                    <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'normal' }}>
                        {hint}
                    </span>
                ) : null}
            </span>
        </label>
    );
}

function RadioRow({ name, value, current, onChange, label }) {
    return (
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: 'var(--text-color)' }}>
            <input
                type="radio"
                name={name}
                value={value}
                checked={current === value}
                onChange={() => onChange(value)}
                style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
            />
            <span>{label}</span>
        </label>
    );
}

function ColorField({ label, value, onChange, disabled }) {
    const safe = /^#[0-9a-fA-F]{6}$/.test(value || '') ? value : '#ffffff';
    return (
        <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12px',
            color: 'var(--text-secondary)',
            marginBottom: '8px',
            opacity: disabled ? 0.55 : 1,
        }}>
            <span style={{ minWidth: '92px' }}>{label}</span>
            <input
                type="color"
                value={safe}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                style={{
                    width: '32px',
                    height: '28px',
                    border: 'none',
                    padding: 0,
                    background: 'transparent',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                }}
            />
            <span style={{ fontSize: '12px', color: 'var(--text-color)' }}>{safe}</span>
        </label>
    );
}

function FontPicker({ label, value, onChange, fonts, fontFamilyCssForName, defaultFontFamilyCss, open, onToggle, onClose }) {
    const selected = (fonts || []).find((font) => String(font._id) === String(value));
    const rowStyle = (active) => ({
        padding: '6px 8px',
        cursor: 'pointer',
        fontSize: '12px',
        backgroundColor: active ? 'var(--button-bg)' : 'transparent',
        color: active ? 'var(--button-text)' : 'var(--text-color)',
    });
    return (
        <div style={{ marginBottom: '8px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{label}</div>
            <button
                type="button"
                onClick={onToggle}
                style={{
                    width: '100%',
                    padding: '6px 8px',
                    borderRadius: '6px',
                    border: '1px solid var(--input-border)',
                    background: 'var(--input-bg)',
                    color: 'var(--input-text)',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    textAlign: 'left',
                    gap: '8px',
                }}
            >
                <span style={{
                    fontFamily: value ? fontFamilyCssForName(selected?.name) : defaultFontFamilyCss,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                    {value ? (selected?.name || 'Select font...') : 'Default (TikTok medium)'}
                </span>
                <span style={{ fontSize: '10px', opacity: 0.7, flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
            </button>
            {open && (
                <div style={{
                    border: '1px solid var(--input-border)',
                    borderRadius: '6px',
                    marginTop: '4px',
                    maxHeight: '160px',
                    overflowY: 'auto',
                    backgroundColor: 'var(--input-bg)',
                }}>
                    <div style={rowStyle(!value)} onClick={() => { onChange(''); onClose(); }}>
                        <span style={{ fontFamily: defaultFontFamilyCss }}>Default (TikTok medium)</span>
                    </div>
                    {(fonts || []).map((font) => (
                        <div
                            key={font._id}
                            style={{ ...rowStyle(String(value) === String(font._id)), fontFamily: fontFamilyCssForName(font.name) }}
                            onClick={() => { onChange(font._id); onClose(); }}
                        >
                            {font.name}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function titleCardStyleOf(settings) {
    if (settings.fontMixing) return 'style1';
    if (settings.centerStackOpening) return 'style2';
    if (settings.centerStackLineByLine) return 'style3';
    return 'standard';
}

/**
 * Per-video override of the workspace/device video settings.
 * Off: generation keeps using the live workspace settings.
 * On: this video uses its own copy, starting from those settings.
 */
export default function BatchVideoSettingsOverride({
    scriptId,
    scriptType,
    enabled,
    settings,
    onToggle,
    onChange,
    onReset,
    isBoukar,
    fonts,
    defaultFontFamilyCss,
    fontFamilyCssForName,
}) {
    const [openMenu, setOpenMenu] = useState(null);
    const isText = scriptType === 'text';
    const id = String(scriptId || 'video');
    const patch = (next) => onChange(next);

    return (
        <div style={{ marginBottom: '10px' }} data-batch-video-settings={id}>
            <button
                type="button"
                aria-pressed={enabled}
                onClick={onToggle}
                style={{
                    width: '100%',
                    padding: '6px 8px',
                    borderRadius: '6px',
                    border: enabled ? '1px solid var(--button-bg)' : '1px solid var(--input-border)',
                    background: enabled ? 'var(--button-bg)' : 'var(--input-bg)',
                    color: enabled ? 'var(--button-text)' : 'var(--text-color)',
                    fontWeight: 600,
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                }}
            >
                <Settings size={13} />
                {enabled ? 'Custom video settings on' : 'Custom video settings'}
            </button>
            <div style={{ marginTop: '4px', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.35 }}>
                {enabled
                    ? 'Overrides the workspace settings on this device for this video only.'
                    : 'Using the workspace settings on this device.'}
            </div>

            {enabled && settings && (
                <div style={{
                    marginTop: '8px',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid var(--input-border)',
                    backgroundColor: 'var(--input-bg)',
                    maxHeight: '340px',
                    overflowY: 'auto',
                }}>
                    <SectionLabel first>Subtitle</SectionLabel>
                    <CheckRow
                        checked={isText ? true : settings.enableSubtitles}
                        disabled={isText}
                        onChange={(value) => patch({ enableSubtitles: value })}
                        label="Enable subtitles"
                        hint={isText ? 'Text-on-screen videos always include subtitles.' : null}
                    />
                    {(isText || settings.enableSubtitles) && (
                        <>
                            <FontPicker
                                label="Subtitle font"
                                value={settings.selectedFontId}
                                onChange={(selectedFontId) => patch({ selectedFontId })}
                                fonts={fonts}
                                fontFamilyCssForName={fontFamilyCssForName}
                                defaultFontFamilyCss={defaultFontFamilyCss}
                                open={openMenu === 'subtitle'}
                                onToggle={() => setOpenMenu((prev) => (prev === 'subtitle' ? null : 'subtitle'))}
                                onClose={() => setOpenMenu(null)}
                            />
                            <ColorField
                                label="Font color"
                                value={settings.subtitleFontColor}
                                onChange={(subtitleFontColor) => patch({ subtitleFontColor })}
                            />
                            <CheckRow
                                checked={settings.subtitleOutlineEnabled}
                                onChange={(subtitleOutlineEnabled) => patch({ subtitleOutlineEnabled })}
                                label="Outline"
                            />
                            <ColorField
                                label="Outline color"
                                value={settings.subtitleOutlineColor}
                                disabled={!settings.subtitleOutlineEnabled}
                                onChange={(subtitleOutlineColor) => patch({ subtitleOutlineColor })}
                            />
                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Subtitle size</div>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <RadioRow name={`${id}-sub-size`} value="small" current={settings.bigText ? 'large' : 'small'} onChange={() => patch({ bigText: false })} label="Small" />
                                    <RadioRow name={`${id}-sub-size`} value="large" current={settings.bigText ? 'large' : 'small'} onChange={() => patch({ bigText: true })} label="Large" />
                                </div>
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Subtitle position</div>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <RadioRow name={`${id}-sub-pos`} value="center" current={settings.subtitlePosition} onChange={(subtitlePosition) => patch({ subtitlePosition })} label="Center" />
                                    <RadioRow name={`${id}-sub-pos`} value="bottom" current={settings.subtitlePosition} onChange={(subtitlePosition) => patch({ subtitlePosition })} label="Bottom" />
                                </div>
                            </div>
                            <CheckRow
                                checked={settings.oneWordPerSubtitle}
                                onChange={(oneWordPerSubtitle) => patch({ oneWordPerSubtitle })}
                                label="1 word per subtitle"
                            />
                            {isBoukar && (
                                <>
                                    <CheckRow
                                        checked={settings.glowText}
                                        onChange={(glowText) => patch({ glowText })}
                                        label="Glow text"
                                    />
                                    {settings.glowText && (
                                        <div style={{ display: 'flex', gap: '12px', margin: '0 0 8px 22px' }}>
                                            <RadioRow name={`${id}-glow`} value="white" current={settings.glowTextColor} onChange={(glowTextColor) => patch({ glowTextColor })} label="White" />
                                            <RadioRow name={`${id}-glow`} value="black" current={settings.glowTextColor} onChange={(glowTextColor) => patch({ glowTextColor })} label="Black" />
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    <SectionLabel>Video</SectionLabel>
                    {!isText && (
                        <div style={{ marginBottom: '8px' }}>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Cut length</div>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <RadioRow name={`${id}-cut`} value="1" current={settings.fastPacing ? '1' : '2'} onChange={() => patch({ fastPacing: true })} label="1 sec" />
                                <RadioRow name={`${id}-cut`} value="2" current={settings.fastPacing ? '1' : '2'} onChange={() => patch({ fastPacing: false })} label="2 sec" />
                            </div>
                        </div>
                    )}
                    {isBoukar && !isText && (
                        <CheckRow checked={settings.speedUp} onChange={(speedUp) => patch({ speedUp })} label="Speed up (1.15x)" />
                    )}
                    {!isText && (
                        <CheckRow checked={settings.flipbookIntro} onChange={(flipbookIntro) => patch({ flipbookIntro })} label="Fast-cut intro" />
                    )}
                    {isBoukar && (isText || settings.enableSubtitles) && (
                        <>
                            <CheckRow
                                checked={settings.openingLineInCenter}
                                onChange={(openingLineInCenter) => patch({ openingLineInCenter })}
                                label="Title card"
                                hint="First sentence becomes the title"
                            />
                            {settings.openingLineInCenter && (
                                <div style={{ margin: '0 0 8px 12px', paddingLeft: '10px', borderLeft: '2px solid var(--border-primary)' }}>
                                    <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                                        <RadioRow name={`${id}-title-size`} value="big" current={settings.openingLineTextSize} onChange={(openingLineTextSize) => patch({ openingLineTextSize })} label="Big" />
                                        <RadioRow name={`${id}-title-size`} value="small" current={settings.openingLineTextSize} onChange={(openingLineTextSize) => patch({ openingLineTextSize })} label="Small" />
                                    </div>
                                    <FontPicker
                                        label="Title card font"
                                        value={settings.openingLineFontId}
                                        onChange={(openingLineFontId) => patch({ openingLineFontId })}
                                        fonts={fonts}
                                        fontFamilyCssForName={fontFamilyCssForName}
                                        defaultFontFamilyCss={defaultFontFamilyCss}
                                        open={openMenu === 'title'}
                                        onToggle={() => setOpenMenu((prev) => (prev === 'title' ? null : 'title'))}
                                        onClose={() => setOpenMenu(null)}
                                    />
                                    <ColorField
                                        label="Title color"
                                        value={settings.openingLineFontColor}
                                        onChange={(openingLineFontColor) => patch({ openingLineFontColor })}
                                    />
                                    <div style={{ marginBottom: '8px' }}>
                                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Style</div>
                                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                            <RadioRow
                                                name={`${id}-title-style`}
                                                value="standard"
                                                current={titleCardStyleOf(settings)}
                                                onChange={() => patch({ fontMixing: false, centerStackOpening: false, centerStackLineByLine: false })}
                                                label="Standard"
                                            />
                                            {!isText && (
                                                <RadioRow
                                                    name={`${id}-title-style`}
                                                    value="style1"
                                                    current={titleCardStyleOf(settings)}
                                                    onChange={() => patch({ fontMixing: true, centerStackOpening: false, centerStackLineByLine: false })}
                                                    label="Style 1"
                                                />
                                            )}
                                            <RadioRow
                                                name={`${id}-title-style`}
                                                value="style2"
                                                current={titleCardStyleOf(settings)}
                                                onChange={() => patch({ fontMixing: false, centerStackOpening: true, centerStackLineByLine: false })}
                                                label="Style 2"
                                            />
                                            <RadioRow
                                                name={`${id}-title-style`}
                                                value="style3"
                                                current={titleCardStyleOf(settings)}
                                                onChange={() => patch({ fontMixing: false, centerStackOpening: false, centerStackLineByLine: true })}
                                                label="Style 3"
                                            />
                                        </div>
                                    </div>
                                    {settings.fontMixing && !isText && (
                                        <ColorField
                                            label="Second color"
                                            value={settings.fontMixingSecondColor}
                                            onChange={(fontMixingSecondColor) => patch({ fontMixingSecondColor })}
                                        />
                                    )}
                                </div>
                            )}
                        </>
                    )}
                    {isBoukar && (
                        <>
                            <CheckRow checked={settings.boukisIntro} onChange={(boukisIntro) => patch({ boukisIntro })} label="Bouki's intro" />
                            {settings.boukisIntro && (
                                <div style={{ display: 'flex', gap: '12px', margin: '0 0 8px 22px' }}>
                                    <RadioRow name={`${id}-bouki`} value="high" current={settings.boukisIntroPitch} onChange={(boukisIntroPitch) => patch({ boukisIntroPitch })} label="High pitch" />
                                    <RadioRow name={`${id}-bouki`} value="low" current={settings.boukisIntroPitch} onChange={(boukisIntroPitch) => patch({ boukisIntroPitch })} label="Low pitch" />
                                </div>
                            )}
                        </>
                    )}

                    <SectionLabel>Audio</SectionLabel>
                    {isBoukar && !isText && (
                        <>
                            <CheckRow checked={settings.cutSilenceMode} onChange={(cutSilenceMode) => patch({ cutSilenceMode })} label="Cut silence in voiceover" />
                            <CheckRow checked={settings.cutMoreSilenceMode} onChange={(cutMoreSilenceMode) => patch({ cutMoreSilenceMode })} label="Cut more silence" />
                            <CheckRow checked={settings.aiSoundEffects} onChange={(aiSoundEffects) => patch({ aiSoundEffects })} label="AI sound effects" />
                        </>
                    )}
                    <CheckRow checked={settings.useMusic} onChange={(useMusic) => patch({ useMusic })} label="Use background music" />

                    <button
                        type="button"
                        onClick={onReset}
                        style={{
                            marginTop: '4px',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid var(--input-border)',
                            background: 'transparent',
                            color: 'var(--text-secondary)',
                            fontSize: '11px',
                            cursor: 'pointer',
                        }}
                    >
                        Reset to current workspace settings
                    </button>
                </div>
            )}
        </div>
    );
}
